/* eslint-disable consistent-return */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, Col, CustomInput, Row } from 'reactstrap';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import history from '~/services/history';

import Pagination from '~/components/Pagination';

import api from '~/services/api';
import SearchBox from '~/components/SearchBox';
import DropdownFilter from '~/components/DropdownFilter';
import DeleteModal from '~/components/Modals/DeleteModal';
import { breakString } from '~/util/newLayoutHelpers';

import {
  deliveryStatuses,
  salesSubStatuses,
  salesStatuses,
} from '~/util/filters';
import Loading from '~/components/Loading';
// import InputSelectAsyncFilter from '~/components/InputSelectAsyncFilter';
import { paymentTypes } from '~/util/translatePaymentWay';
import InputMaskFilter from '~/components/InputMaskFilter';
import { getRole } from '~/util/getRole';
// import TextFilter from '~/components/TextFilter';
import useRoles from '~/hooks/useRoles';
import { setSearch } from '~/store/modules/search/actions';
import SaleInfoCard from './SaleInfoCard';

const SalesList = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();

  const mountedRef = useRef(true);

  const [sales, setSales] = useState(null);
  const subscriber = useSelector(
    ({ user }) => user?.profile?.subscriber?.fantasy_name
  );
  const searchQuery = useSelector(({ search }) => search.query);
  const [allSales, setAllSales] = useState(false);
  const [selectedSales, setSelectedSales] = useState([]);
  const [filters, setFilters] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(null);
  const [deliveryFilter, setDeliveryFilter] = useState(null);
  const [statusFiltered, setStatusFiltered] = useState(null);
  const [statusFilter, setStatusFilter] = useState(state?.salesStatus || null);
  const [crossSale, setCrossSale] = useState(false);
  // const [clientFilter, ] = useState(null);
  // const [storeFilter, ] = useState(null);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState(null);
  const [deliveryFiltered, setDeliveryFiltered] = useState();
  const [createdAtFilter, setCreatedAtFilter] = useState(null);
  const [pagination, setPagination] = useState(
    { current_page: state?.page } || null
  );
  const [loading, setLoading] = useState(true);
  // const [storeQuery, setStoreQuery] = useState('');

  const [saleToBeDeleted, setSaleToBeDeleted] = useState(null);
  const [showDeleteSaleModal, setShowDeleteSaleModal] = useState(false);
  const width = window.innerWidth;

  const parseStatusFilter = raw => {
    if (raw === null || raw === undefined || raw === '') {
      return { status: null, status_text: null, raw };
    }

    // Quando vier do Dropdown como número, normaliza pra string
    const str = String(raw);

    // Substatus: "0|EM_ANALISE"
    if (str.includes('|')) {
      const [statusStr, statusTextValue] = str.split('|');
      return {
        status: Number(statusStr),
        status_text: statusTextValue || null,
        raw: str,
      };
    }

    // Status numérico normal
    return { status: Number(str), status_text: null, raw: str };
  };

  const parsedStatus = useMemo(
    () => parseStatusFilter(statusFilter),
    [statusFilter]
  );

  const statusText = useMemo(() => {
    const statusObj = salesStatuses.find(s => s.value === parsedStatus.status);
    if (statusObj?.value === 13 || statusObj?.value === 14) {
      return statusObj?.name;
    }
    if (statusObj?.value === 0 || statusObj?.value === 1) {
      return;
    }
    return statusObj?.label;
  }, [parsedStatus.status]);

  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  const { isSalesman, isConsultant, isCrossManager, isAdmin } = useRoles();

  const showSellerInfo =
    (!isSalesman && !isConsultant && !isCrossManager) || isAdmin;

  const getURL = useMemo(() => {
    if (parsedStatus.status === 12) {
      return `${role}/carts?status[]=0&status[]=1&status[]=2&status[]=3`;
    }
    return `${role}/carts`;
  }, [parsedStatus.status, role]);

  // const getSalesmansURL = useMemo(() => {
  //   if (isAdmin) {
  //     return '/administrator/users';
  //   }
  //   if (isStoreManager) {
  //     return '/store-manager/users';
  //   }
  //   if (isSalesman) {
  //     return '/salesman/users/coworkers';
  //   }
  //   return null;
  // }, [isStoreManager, isAdmin, isSalesman]);

  const deleteURL = useMemo(() => {
    return `${role}/carts/${saleToBeDeleted?.id}`;
  }, [role, saleToBeDeleted]);

  const params = useMemo(() => {
    const statusParam =
      parsedStatus.status !== null && parsedStatus.status < 7
        ? parsedStatus.status
        : null;

    const statusTextParam =
      parsedStatus.status_text ||
      (parsedStatus.status === 0 ||
      parsedStatus.status === 1 ||
      (parsedStatus.status >= 7 && parsedStatus.status < 12) ||
      parsedStatus.status >= 13
        ? statusText /* seu statusText atual, se ainda fizer sentido */
        : null);

    return {
      query: searchQuery || null,
      status: statusParam,
      status_text: statusTextParam,
      is_cross: crossSale || null,
      payment_type: paymentTypeFilter,
      created_at: createdAtFilter,
      delivery_method: deliveryFilter,
      page:
        state?.page || pagination?.current_page > 1
          ? pagination?.current_page
          : null,
    };
  }, [
    parsedStatus.status,
    parsedStatus.status_text,
    pagination,
    searchQuery,
    crossSale,
    paymentTypeFilter,
    createdAtFilter,
    deliveryFilter,
    state,
    statusText,
  ]);

  const setData = data => {
    if (data) {
      const carts = data.carts.map(cart => ({
        ...cart,
        formatted_updated_at: format(
          parseISO(cart.updated_at),
          "dd/MMM/yy - HH'h'mm",
          {
            locale: ptBR,
          }
        ),
        client: cart.client
          ? {
              ...cart.client,
              broken_name: cart.client
                ? breakString(cart.client?.name, ' ', 18)
                : '',
            }
          : {},
        seller: cart.created_by
          ? {
              ...cart.created_by,
              broken_name: cart.created_by
                ? breakString(cart.created_by?.name, ' ', 18)
                : '',
            }
          : {},
        handled: cart.handled_by
          ? {
              ...cart.handled_by,
              broken_name: cart.handled_by
                ? breakString(cart.handled_by?.name, ' ', 18)
                : '',
            }
          : {},
        store: cart.store
          ? {
              ...cart.store,
              broken_fantasy_name: breakString(
                cart.store?.fantasy_name,
                ' ',
                18,
                true
              ),
            }
          : {},
      }));
      setSales(carts);
      setPagination(data.meta);
      setFilters(data.filters);
    }
  };

  const loadSales = useCallback(async () => {
    if (!mountedRef.current) return null;

    const { data } = await api.get(getURL, {
      params: {
        ...params,
        query: params.query || '',
      },
    });

    if (!mountedRef.current) return null;

    setData(data);
    history.replace();
  }, [params, getURL]);

  useEffect(() => {
    if (searchQuery) {
      return () => {};
    }

    const refreshInterval = setInterval(loadSales, 40 * 1000);

    return () => clearInterval(refreshInterval);
  }, [loadSales, sales, searchQuery]);

  useEffect(() => {
    dispatch(setSearch({ query: '' }));

    return () => {
      mountedRef.current = false;
    };
  }, [dispatch]);

  // useEffect(() => {
  //   let interval = null;
  //   if (!loading) {
  //     interval = setInterval(
  //       async () => {
  //         const { data } = await api.get(getURL, { params });
  //         if (
  //           componentReqRunningRef.current === false &&
  //           letIntervalRef.current === true
  //         ) {
  //           setData(data);
  //         } else if (
  //           componentReqRunningRef.current === false &&
  //           letIntervalRef.current === false
  //         ) {
  //           setLetInterval(true);
  //         }
  //       },
  //       5 * 1000,
  //       []
  //     );
  //   } else {
  //     clearInterval(interval);
  //   }

  //   return () => {
  //     clearInterval(interval);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [loading, getURL, params]);

  const handleSelectOrUnselectSale = (sale, isSelected) => {
    if (isSelected) {
      setSelectedSales(selectedSales.filter(s => s.id !== sale.id));
    } else {
      setSelectedSales([...selectedSales, sale]);
    }
  };

  useEffect(() => {
    if (allSales) {
      setSelectedSales(sales);
    } else {
      setSelectedSales([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSales]);

  useEffect(() => {
    if (filters !== null) {
      const allSituationOptions = [...salesStatuses, ...salesSubStatuses];

      const getSituationTypes = allSituationOptions.filter(opt => {
        const v = String(opt.value);

        // Substatus "0|XXXX" só entra se o backend permitir status 0
        if (v.includes('|')) return filters?.status?.includes(0);

        // Status normal (numérico): respeita o que o backend liberou
        return filters?.status?.includes(opt.value);
      });

      const getPaymentTypes = paymentTypes.filter(types =>
        filters.payment_types.includes(types.value)
      );

      const getDeliveryTypes = deliveryStatuses.filter(types =>
        filters.delivery_methods.includes(types.value)
      );

      const getDeliveryTypesWithSubscriber = getDeliveryTypes.map(type =>
        type.value === 'PROPRIA' ? { ...type, label: `Alternativa` } : type
      );

      setDeliveryFiltered(getDeliveryTypesWithSubscriber);
      setPaymentFilter(getPaymentTypes);
      setStatusFiltered(getSituationTypes);
    }
  }, [filters, subscriber]);

  // Estilos do container que controla o layout do switch e label
  const containerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '8px',
  };

  // Estilos do container que mantém o switch e o label juntos
  const switchContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '5px',
  };

  // Estilos do label "Vendas Cross"
  const labelStyles = {
    fontSize: '16px !important',
    margin: '10px',
  };

  const switchWrapperStyles = {
    display: 'flex',
    justifyContent: 'flex-end',
    border: 'none',
  };

  // Estilos do switch com base no estado crossSale
  const switchStyles = {
    marginLeft: 'auto',
  };

  return (
    <>
      <Row className="w-100 mx-0 mb-4 d-block d-xl-none">
        <Col className="px-0 mt-3 pt-1 mb-0 my-lg-0" xs={12} xl={6}>
          <SearchBox
            url={getURL}
            params={params}
            query={searchQuery}
            setQuery={q => dispatch(setSearch({ query: q }))}
            setData={setData}
            setLoading={setLoading}
          />
        </Col>
      </Row>
      <Row className="w-100 mx-0 mb-2 mb-lg-4 d-flex flex-row align-items-center filters">
        <span className="text-left mt-0 mb-2 mb-lg-0 text-muted font-size-14 mr-2 filter-text-desktop">
          Filtros
        </span>
        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <DropdownFilter
            name="sales-status-filter"
            id="sales-status-filter"
            placeholder="Situação"
            options={statusFiltered}
            url={getURL}
            params={params}
            setData={setData}
            setLoading={setLoading}
            filter={statusFilter}
            setFilter={setStatusFilter}
            query={searchQuery}
            setQuery={q => dispatch(setSearch({ query: q }))}
            clearable
            width={width > 399 ? 180 : 340}
            menuListWidth={width > 399 ? 250 : 340}
          />
        </Col>
        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <DropdownFilter
            name="payment-type-filter"
            id="payment-type-filter"
            placeholder="Tipo de Pagamento"
            options={paymentFilter}
            url={getURL}
            params={params}
            setData={setData}
            setLoading={setLoading}
            filter={paymentTypeFilter}
            setFilter={setPaymentTypeFilter}
            query={searchQuery}
            setQuery={q => dispatch(setSearch({ query: q }))}
            clearable
            width={width > 399 ? 180 : 340}
            menuListWidth={width > 399 ? 250 : 340}
          />
        </Col>

        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <DropdownFilter
            name="delivery-type-filter"
            id="delivery-type-filter"
            placeholder="Tipo de Entrega"
            options={deliveryFiltered}
            url={getURL}
            params={params}
            setData={setData}
            setLoading={setLoading}
            filter={deliveryFilter}
            setFilter={setDeliveryFilter}
            query={searchQuery}
            setQuery={q => dispatch(setSearch({ query: q }))}
            clearable
            width={width > 399 ? 180 : 340}
            menuListWidth={width > 399 ? 250 : 340}
          />
        </Col>

        {/* <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <TextFilter
            name="client-filter"
            id="client-filter"
            itemsname="clients"
            placeholder="Cliente"
            url={getURL}
            params={params}
            query={clientFilter}
            setQuery={setClientFilter}
            setLoading={setLoading}
            setData={setData}
          />
        </Col>
        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <TextFilter
            name="sales-store-filter"
            id="sales-store-filter"
            itemsname="stores"
            placeholder="Loja"
            url={getURL}
            params={params}
            query={storeFilter}
            setQuery={setStoreFilter}
            setLoading={setLoading}
            setData={setData}
          />
          <Input
            placeholder="Loja"
            value={storeQuery}
            onChange={({ target: { value } }) => setStoreQuery(value)}
            className="store-in-page-filter"
          />
          <DropdownFilter
            name="sales-store-filter"
            id="sales-store-filter"
            placeholder="Loja"
            options={userStores}
            url={getURL}
            params={params}
            setData={setData}
            setLoading={setLoading}
            filter={storeFilter}
            setFilter={setStoreFilter}
            query={searchQuery}
            setQuery={setSearchQuery}
            setReqRunning={setComponentReqRunning}
            clearable
            width={100}
            menuListWidth={width > 399 ? 250 : 340}
          />
        </Col> */}

        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <InputMaskFilter
            name="created_at-filter"
            id="created_at-filter"
            isDateType
            placeholder="Data"
            mask="99/99/9999"
            generateFilterParams={date => ({ created_at: date })}
            params={params}
            url={getURL}
            setLoading={setLoading}
            setData={setData}
            setFilter={setCreatedAtFilter}
            filter={createdAtFilter}
            style={width > 399 ? { width: '180px' } : { width: '340px' }}
          />
        </Col>

        <Col xs="auto" className="pl-0 pr-2 mb-2 mb-lg-0">
          <CustomInput
            type="switch"
            id="crossSaleSwitch"
            name="crossSale"
            label="Vendas Cross"
            checked={crossSale}
            className="float-right form-control input-color mask-filter"
            style={switchStyles}
            onChange={() => setCrossSale(!crossSale)}
          />
        </Col>

        <Col
          xs="auto"
          className="ml-auto d-flex flex-column justify-items-end px-0"
        >
          <Button
            className="new-layout-btn main"
            color="primary"
            size="lg"
            onClick={() => history.push('/registrar')}
          >
            <i className="las la-plus font-size-16 align-middle mr-2" />
            Inserir Venda
          </Button>
        </Col>
      </Row>
      {loading ? (
        <Loading />
      ) : (
        <>
          {sales?.length > 0 ? (
            <>
              <Row className="w-100 mx-0 mb-4 px-0 d-lg-none">
                <Col
                  xs={12}
                  className="mb-0 d-flex justify-content-between align-items-center switch-cross"
                  style={containerStyles}
                >
                  <div style={switchContainerStyles}>
                    <label htmlFor="crossSaleSwitch" style={labelStyles}>
                      Vendas Cross
                    </label>
                    <div style={switchWrapperStyles}>
                      <CustomInput
                        type="switch"
                        id="crossSaleSwitch"
                        name="crossSale"
                        checked={crossSale}
                        className="float-right"
                        style={switchStyles}
                        onChange={() => setCrossSale(!crossSale)}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="w-100 mx-0 mb-4 d-none d-lg-flex px-3">
                <Col xs={1} align="center" className="d-none">
                  <CustomInput
                    type="checkbox"
                    id="allSales"
                    className="text-primary z-index-0"
                    checked={allSales}
                    defaultValue={allSales}
                    onChange={() => setAllSales(!allSales)}
                  />
                </Col>
                <Col xs={showSellerInfo ? 1 : 2} align="start">
                  <div className="new-layout-table-column-label">Pedido</div>
                </Col>
                {showSellerInfo && (
                  <Col
                    xs={2}
                    align="start"
                    // style={{ paddingLeft: '1.25rem' }}
                  >
                    <div className="new-layout-table-column-label">
                      Gerenciado Por
                    </div>
                  </Col>
                )}
                <Col
                  xs={1}
                  align="start"
                  // style={{ paddingLeft: '1.25rem' }}
                >
                  <div className="new-layout-table-column-label">Cliente</div>
                </Col>
                <Col xs={2} align="start">
                  <div className="new-layout-table-column-label">Loja</div>
                </Col>
                <Col xs={1} align="start">
                  <div className="new-layout-table-column-label">Valor</div>
                </Col>
                <Col xs={2} align="start">
                  <div className="new-layout-table-column-label d-flex flex-row flex-wrap w-100">
                    Últ. Atualização
                  </div>
                </Col>
                <Col xs={showSellerInfo ? 2 : 3} align="center">
                  <div className="new-layout-table-column-label">Situação</div>
                </Col>
                <Col xs={1} align="center">
                  <div className="new-layout-table-column-label">Ações</div>
                </Col>
              </Row>
              {sales
                ?.filter(sale => !crossSale || sale.is_cross)
                ?.filter(
                  sale => sale?.store?.fantasy_name?.toLowerCase()
                  // .includes(storeQuery.toLowerCase())
                )
                .map(sale => {
                  const isSelected = selectedSales.some(s => s.id === sale.id);
                  return (
                    <SaleInfoCard
                      key={sale.id}
                      sale={sale}
                      isSelected={isSelected}
                      onSelect={handleSelectOrUnselectSale}
                      onDelete={incomingSale => {
                        setSaleToBeDeleted(incomingSale);
                        setShowDeleteSaleModal(true);
                      }}
                      showInfoSeller={showSellerInfo}
                      params={params}
                    />
                  );
                })}

              <Row className="w-100 mx-0 mb-4">
                <Col className="px-0" align="end">
                  {pagination && (
                    <div className="mb-3">
                      <Pagination
                        paginationMeta={pagination}
                        url={getURL}
                        setData={setData}
                        setLoading={setLoading}
                        query={searchQuery}
                        params={params}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </>
          ) : (
            <h3 style={{ minHeight: '700px' }}>Nenhuma venda encontrada.</h3>
          )}
        </>
      )}
      <DeleteModal
        show={showDeleteSaleModal}
        setShow={setShowDeleteSaleModal}
        deleteUrl={deleteURL}
        getUrl={getURL}
        params={params}
        setData={setData}
        itemsArray={sales}
        pagination={pagination}
        query={searchQuery}
        setItemToBeDeleted={setSaleToBeDeleted}
        headerText="Deseja mesmo excluir a venda?"
        bodyText="Essa ação irá excluir a venda"
        itemName={`#${saleToBeDeleted?.order_number}`}
        successMsg="Venda excluída com sucesso!"
      />
    </>
  );
};

export default SalesList;
